import { T, Var, useGT } from 'gt-react';
import type { BuyContractPriceOption } from '@erp/rpc';
import type { LegPriceBreak } from '@erp/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatUnitCost } from './unitCostFormat';

/**
 * Lets the buyer pick which active contract prices a buy line (GEN-2956).
 * Shown only when more than one contract applies at the current quantity.
 */
export function BuyContractPriceSelect({
  options,
  priceBreak,
  disabled,
  onSelect,
}: {
  options: readonly BuyContractPriceOption[];
  priceBreak: LegPriceBreak | null | undefined;
  disabled: boolean;
  onSelect: (costAgreementId: string) => void;
}) {
  const gt = useGT();
  if (options.length <= 1) return null;

  const contractPriceLabel = gt('Contract price', {
    context:
      'replenishment board contract price picker: label and placeholder for choosing which contract prices the buy',
  });

  // A line priced off a price break or list shows no contract as chosen, even
  // when contracts apply; `null` keeps the Select controlled and empty.
  const selectedId =
    priceBreak?.costSource === 'agreement'
      ? (priceBreak.costAgreementId ?? null)
      : null;

  return (
    <Select value={selectedId} disabled={disabled} onValueChange={onSelect}>
      <SelectTrigger
        className="h-7 w-full min-w-[8rem] max-w-full border-gray-200 bg-white px-2 text-[11px] font-normal"
        aria-label={contractPriceLabel}
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <SelectValue placeholder={contractPriceLabel} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem
            key={option.costAgreementId}
            value={option.costAgreementId}
            className="text-xs *:[span]:last:flex-1"
          >
            <T context="replenishment board contract price option: contract number; its unit cost at this quantity shows right-aligned after it">
              Contract <Var>{String(option.contractNumber)}</Var>
            </T>
            <span className="ml-auto tabular-nums">
              {formatUnitCost(option.unitCost)}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
